import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { X, Play, Pause, RotateCcw, Share2, Loader2, AlertCircle } from 'lucide-react';
import { BarRaceStage } from './BarRaceStage';
import { raceDuration } from '../../utils/barRace';
import { api } from '../../services/api';
import { trackEvent } from '../../utils/analytics';
import type { RankingTimelineData } from '../../types';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** rAF-driven playback clock: play/pause/scrub/replay, stops at the end. */
function useRafClock(duration: number) {
  const [t, setTState] = useState(0);
  const [playing, setPlaying] = useState(false);
  const tRef = useRef(0);
  const raf = useRef<number | null>(null);
  const last = useRef<number | null>(null);

  const setT = useCallback((v: number) => {
    tRef.current = v;
    setTState(v);
  }, []);

  useEffect(() => {
    if (!playing) return;
    last.current = null;
    const tick = (now: number) => {
      if (last.current == null) last.current = now;
      const dt = now - last.current;
      last.current = now;
      const next = tRef.current + dt;
      if (next >= duration) {
        setT(duration);
        setPlaying(false);
        return;
      }
      setT(next);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [playing, duration, setT]);

  return { t, setT, playing, setPlaying };
}

type ShareState = 'idle' | 'processing' | 'error';

export interface RankingEvolutionViewProps {
  rankingId: string;
  rankingName: string;
  rankingImageUrl?: string | null;
  /** Pre-fetched timeline (playground/tests). When set, no API fetch happens. */
  initialData?: RankingTimelineData;
  /** Enables the "Compartilhar vídeo" button (needs a real rankingId). */
  canShare?: boolean;
  onClose: () => void;
}

export function RankingEvolutionView({
  rankingId,
  rankingName,
  rankingImageUrl,
  initialData,
  canShare = true,
  onClose,
}: RankingEvolutionViewProps) {
  const reduceMotion = useReducedMotion();
  const [timeline, setTimeline] = useState<RankingTimelineData | null>(initialData ?? null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);
  const [shareState, setShareState] = useState<ShareState>('idle');

  const duration = timeline ? raceDuration(timeline) : 0;
  const { t, setT, playing, setPlaying } = useRafClock(duration);

  // Fetch the timeline unless it was provided.
  useEffect(() => {
    if (initialData) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.rankings.timeline(rankingId);
        if (cancelled) return;
        if (res.success && res.data) {
          setTimeline(res.data);
        } else {
          setError('Não foi possível carregar a evolução.');
        }
      } catch {
        if (!cancelled) setError('Não foi possível carregar a evolução.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rankingId, initialData]);

  // Autoplay shortly after data lands (or jump to the final frame if the user
  // prefers reduced motion).
  useEffect(() => {
    if (!timeline || duration === 0) return;
    if (reduceMotion) {
      setT(duration);
      setPlaying(false);
      return;
    }
    const id = setTimeout(() => {
      setT(0);
      setPlaying(true);
    }, 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeline, duration, reduceMotion]);

  const atEnd = duration > 0 && t >= duration;

  const handlePlayPause = () => {
    if (atEnd) {
      setT(0);
      setPlaying(true);
    } else {
      setPlaying(!playing);
    }
  };

  const handleReplay = () => {
    setT(0);
    setPlaying(true);
  };

  const handleShare = async () => {
    if (shareState === 'processing') return;
    setShareState('processing');
    try {
      let res = await api.rankings.requestEvolutionVideo(rankingId);
      const startedAt = Date.now();
      while (res.success && res.data?.status === 'processing') {
        if (Date.now() - startedAt > 180000) {
          setShareState('error');
          return;
        }
        await sleep(3000);
        res = await api.rankings.evolutionVideoStatus(rankingId);
      }
      const video = res.success ? res.data : null;
      if (!video || video.status !== 'ready' || !video.url) {
        setShareState('error');
        return;
      }

      trackEvent('btv_ranking_evolution_share');

      const videoUrl = video.url;
      const slug = rankingName.toLowerCase().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'ranking';
      const filename = `evolucao-${slug}.mp4`;

      // Fetch the MP4 once (needs R2 bucket CORS). Then:
      //  - mobile with file-share → native share sheet (video into WhatsApp/IG),
      //  - otherwise (desktop) → download the blob we already have.
      // If the fetch fails (CORS not configured) fall back to opening the URL.
      try {
        const blob = await (await fetch(videoUrl)).blob();
        const file = new File([blob], filename, { type: 'video/mp4' });

        if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: `${rankingName} — Evolução da temporada` });
          setShareState('idle');
          return;
        }

        // No file-share (desktop): trigger a real download of the fetched blob.
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
        setShareState('idle');
        return;
      } catch (err) {
        // User cancelled the share sheet — respected, not an error.
        if (err instanceof DOMException && err.name === 'AbortError') {
          setShareState('idle');
          return;
        }
        // Fetch failed (e.g. CORS) → last resort: open the URL in a new tab.
        window.open(videoUrl, '_blank');
        setShareState('idle');
      }
    } catch {
      // Network/API failure while requesting or polling the video.
      setShareState('error');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 bg-deep-blue flex flex-col"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Close */}
      <button
        onClick={onClose}
        aria-label="Fechar"
        className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
        style={{ top: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      >
        <X className="w-5 h-5" />
      </button>

      {/* Stage */}
      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-7 h-7 text-turquoise animate-spin" />
          </div>
        ) : error || !timeline ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-8 text-center">
            <AlertCircle className="w-8 h-8 text-coral" />
            <p className="text-white/70 text-sm">{error || 'Sem dados de evolução.'}</p>
          </div>
        ) : (
          <BarRaceStage
            timeline={timeline}
            tMs={t}
            rankingName={rankingName}
            rankingImageUrl={rankingImageUrl}
          />
        )}
      </div>

      {/* Controls */}
      {timeline && !loading && !error && (
        <div className="shrink-0 px-5 pt-2 pb-5 space-y-3">
          {/* Scrubber */}
          <input
            type="range"
            min={0}
            max={duration}
            step={16}
            value={t}
            onChange={(e) => {
              setPlaying(false);
              setT(Number(e.target.value));
            }}
            className="w-full accent-turquoise h-1.5 cursor-pointer"
            aria-label="Linha do tempo"
          />

          <div className="flex items-center gap-3">
            <button
              onClick={handlePlayPause}
              className="w-11 h-11 rounded-full bg-turquoise text-deep-blue flex items-center justify-center shrink-0 hover:brightness-110 transition"
              aria-label={playing ? 'Pausar' : 'Reproduzir'}
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>

            <button
              onClick={handleReplay}
              className="w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center shrink-0 hover:bg-white/20 transition"
              aria-label="Repetir"
            >
              <RotateCcw className="w-5 h-5" />
            </button>

            {canShare && (
              <button
                onClick={handleShare}
                disabled={shareState === 'processing'}
                className="flex-1 h-11 rounded-full bg-sunny text-deep-blue font-semibold flex items-center justify-center gap-2 hover:brightness-105 transition disabled:opacity-70"
              >
                {shareState === 'processing' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Gerando vídeo… ~1 min
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4" />
                    Compartilhar vídeo
                  </>
                )}
              </button>
            )}
          </div>

          {shareState === 'error' && (
            <p className="text-center text-xs text-coral">
              Não foi possível gerar o vídeo agora. Tente novamente.
            </p>
          )}
        </div>
      )}
    </motion.div>
  );
}
